import type { DBFreelancerSupplier, DBVoucher, FreelancerProjectHistoryRow } from "@/lib/db-types";
import type { FreelancerListRow } from "@/lib/freelancers";

/**
 * VIB-2927: ren logikk for frilans på prosjekt. Funksjonen leser bare; den
 * skriver aldri til budsjettlinjer eller bilag (plan § 3.8).
 */

/**
 * Speiler SQL-funksjonen public.normalize_supplier_name i
 * 20261024155732_vib2927_freelancers_on_projects.sql. Brukes bare for bilag
 * uten po_supplier_id. Endres den ene, må den andre endres likt.
 */
export function normalizeSupplierName(name: string | null | undefined): string | null {
  const s = (name ?? "")
    .toLowerCase()
    .replace(/(?<![\p{L}\p{N}_])(as|asa|enk|da|ans|ab|oy|ltd)(?![\p{L}\p{N}_])/gu, " ")
    .replace(/[^a-z0-9æøåäöéü]+/g, " ")
    .trim();
  return s === "" ? null : s;
}

/** Bilagstyper som kommer fra en leverandør og kan kobles til en frilanser. */
export const FREELANCE_VOUCHER_TYPES = ["IncomingInvoice", "IncomingCreditNote"] as const;

export function isFreelanceVoucher(v: Pick<DBVoucher, "voucher_type" | "po_supplier_id" | "supplier_name">): boolean {
  return (FREELANCE_VOUCHER_TYPES as readonly string[]).includes(v.voucher_type)
    && (v.po_supplier_id != null || normalizeSupplierName(v.supplier_name) != null);
}

/**
 * Leverandørkoblingen et bilag faller inn under. Samme regel som SQL-en:
 * bilag med PO-ID matches på ID, bilag uten på normalisert navn.
 */
export function supplierLinkFor(
  v: Pick<DBVoucher, "po_supplier_id" | "supplier_name">,
  links: readonly DBFreelancerSupplier[],
): DBFreelancerSupplier | null {
  if (v.po_supplier_id != null) return links.find((l) => l.po_supplier_id === v.po_supplier_id) ?? null;
  const key = normalizeSupplierName(v.supplier_name);
  if (!key) return null;
  return links.find((l) => l.po_supplier_id == null && l.supplier_key === key) ?? null;
}

/**
 * Kan linjen ha frilanser? Bare eksterne linjer, og ikke medgått-linjer
 * (de overskrives av trigger og representerer ingen leverandør).
 */
export function canTagFreelancer(bl: { cost_type: string | null; description: string | null; template_name?: string | null }): boolean {
  if (bl.cost_type !== "external") return false;
  return !/\(medgått\)/i.test(`${bl.template_name ?? ""} ${bl.description ?? ""}`);
}

function tokens(text: string): string[] {
  return text.toLowerCase().split(/[^\p{L}\p{N}]+/u).filter((t) => t.length >= 3);
}

/** Fagfeltet ligner linjenavnet: et ord deler de tre første bokstavene («Lysmester» ~ «Lystekniker»). */
export function disciplineMatchesLine(disciplineLabel: string, lineName: string): boolean {
  const line = tokens(lineName);
  return tokens(disciplineLabel).some((d) => line.some((l) => l.slice(0, 3) === d.slice(0, 3)));
}

/** Aktive frilansere, de med fagfelt som ligner linjen først, så vurdering og navn. */
export function rankFreelancersForLine(rows: readonly FreelancerListRow[], lineName: string): { row: FreelancerListRow; matches: boolean }[] {
  const avg = (r: FreelancerListRow) => (r.ratings.length ? r.ratings.reduce((s, x) => s + x, 0) / r.ratings.length : -1);
  return rows
    .filter((r) => !r.archived_at)
    .map((row) => ({ row, matches: row.disciplines.some((d) => disciplineMatchesLine(d.label, lineName)) }))
    .sort((a, b) =>
      Number(b.matches) - Number(a.matches) || avg(b.row) - avg(a.row) || a.row.name.localeCompare(b.row.name, "nb"),
    );
}

/** Siste prosjektbruk per frilanser fra historikken. */
export function lastUsedByFreelancer(rows: readonly FreelancerProjectHistoryRow[]): Map<string, string> {
  const m = new Map<string, string>();
  for (const r of rows) {
    if (!r.last_used) continue;
    const prev = m.get(r.freelancer_id);
    if (!prev || r.last_used > prev) m.set(r.freelancer_id, r.last_used);
  }
  return m;
}
